const select = (selector, root = document) => root.querySelector(selector);
const all = (selector, root = document) => [...root.querySelectorAll(selector)];
const media = matchMedia('(min-width: 901px)');
let cleanup = () => {};
let sdkPromise;
let embeddedPromise;
let viewTracked = false;

function track(name, detail = {}) {
  document.dispatchEvent(new CustomEvent('ea:analytics', { detail: { event: name, ...detail } }));
  const privacy = window.Shopify?.customerPrivacy;
  if (privacy && !privacy.analyticsProcessingAllowed()) return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: name, ...detail });
  if (window.Shopify?.analytics?.publish) {
    Promise.resolve(window.Shopify.analytics.publish(name, detail)).catch(() => {});
  }
}

function loadPostscript(shopId) {
  if (window.postscript?.popups?.open) return Promise.resolve(window.postscript);
  if (sdkPromise) return sdkPromise;
  sdkPromise = new Promise((resolve, reject) => {
    let settled = false;
    let ownedScript;
    const finish = (error) => {
      if (settled) return;
      if (!error && !window.postscript?.popups?.open) return;
      settled = true;
      clearTimeout(timeout);
      clearInterval(poll);
      window.removeEventListener('postscriptReady', ready);
      if (error) {
        ownedScript?.remove();
        sdkPromise = undefined;
        reject(error);
      } else resolve(window.postscript);
    };
    const ready = () => finish();
    const timeout = setTimeout(() => finish(new Error('sdk_timeout')), 12000);
    const poll = setInterval(ready, 100);
    window.addEventListener('postscriptReady', ready);
    if (!document.querySelector('script[src*="sdk.postscript.io/"]')) {
      ownedScript = document.createElement('script');
      ownedScript.src = `https://sdk.postscript.io/sdk.bundle.js?shopId=${encodeURIComponent(shopId)}`;
      ownedScript.async = true;
      ownedScript.onerror = () => finish(new Error('sdk_load'));
      document.head.append(ownedScript);
    }
  });
  return sdkPromise;
}

function loadEmbeddedForm() {
  if (customElements.get('postscript-embedded-form')) return Promise.resolve();
  if (embeddedPromise) return embeddedPromise;
  embeddedPromise = new Promise((resolve, reject) => {
    let settled = false;
    let script = document.querySelector('script[src="https://sdk.postscript.io/embeddedForm.bundle.js"]');
    const finish = error => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      if (error) {
        script?.remove();
        embeddedPromise = undefined;
        reject(error);
      } else resolve();
    };
    const timer = setTimeout(() => finish(new Error('embedded_timeout')), 15000);
    customElements.whenDefined('postscript-embedded-form').then(() => finish());
    if (!script) {
      script = document.createElement('script');
      script.src = 'https://sdk.postscript.io/embeddedForm.bundle.js';
      script.async = true;
      script.onerror = () => finish(new Error('embedded_load'));
      document.head.append(script);
    }
  });
  return embeddedPromise;
}

function setupGalleries(root, listen) {
  let dismiss = () => {};
  all('[data-ea-gallery-open]', root).forEach(trigger => {
    const template = select('[data-ea-gallery-template]', trigger.closest('.ea-product'));
    if (!template || typeof HTMLDialogElement === 'undefined') return;
    trigger.hidden = false;
    listen(trigger, 'click', () => {
      dismiss();
      const dialog = template.content.firstElementChild.cloneNode(true);
      const track = select('[data-ea-gallery-track]', dialog);
      const slides = all('.ea-gallery-slide', track);
      if (!slides.length) return;
      const controller = new AbortController();
      const on = (target, event, handler) => target.addEventListener(event, handler, { signal: controller.signal });
      const dots = select('[data-ea-gallery-dots]', dialog);
      const previous = select('[data-ea-gallery-prev]', dialog);
      const next = select('[data-ea-gallery-next]', dialog);
      const status = select('[data-ea-gallery-status]', dialog);
      const scrollY = window.scrollY;
      const bodyStyle = document.body.style.cssText;
      const htmlOverflow = document.documentElement.style.overflow;
      let current = 0;
      let closed = false;
      function update() {
        all('button', dots).forEach((dot, i) => dot.setAttribute('aria-current', String(i === current)));
        previous.disabled = current === 0;
        next.disabled = current === slides.length - 1;
        status.textContent = `${dots.dataset.label} ${current + 1} / ${slides.length}`;
      }
      function go(index, smooth = true) {
        current = Math.max(0, Math.min(slides.length - 1, index));
        track.scrollTo({ left: current * track.clientWidth, behavior: smooth && !matchMedia('(prefers-reduced-motion: reduce)').matches ? 'smooth' : 'instant' });
        update();
      }
      slides.forEach((slide, index) => {
        const image = select('img', slide);
        image.draggable = false;
        if (index === 0) image.loading = 'eager';
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.setAttribute('aria-label', `${dots.dataset.label} ${index + 1}`);
        dots.append(dot);
        on(dot, 'click', () => go(index));
      });
      select('.ea-gallery-controls', dialog).hidden = slides.length < 2;
      dismiss = () => {
        if (closed) return;
        closed = true;
        controller.abort();
        observer.disconnect();
        dialog.close();
        dialog.remove();
        document.body.style.cssText = bodyStyle;
        document.documentElement.style.overflow = htmlOverflow;
        window.scrollTo({ top: scrollY, behavior: 'instant' });
        if (trigger.isConnected) trigger.focus({ preventScroll: true });
      };
      on(select('[data-ea-gallery-close]', dialog), 'click', () => dismiss());
      on(dialog, 'cancel', event => { event.preventDefault(); dismiss(); });
      on(dialog, 'click', event => {
        const box = dialog.getBoundingClientRect();
        if (event.target === dialog && (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom)) dismiss();
      });
      on(previous, 'click', () => go(current - 1));
      on(next, 'click', () => go(current + 1));
      on(dialog, 'keydown', event => {
        if (event.key === 'Tab') {
          const targets = all('button:not(:disabled), [tabindex="0"]', dialog).filter(el => el.getClientRects().length);
          if (targets.length) {
            event.preventDefault();
            const index = targets.indexOf(document.activeElement);
            targets[(index + (event.shiftKey ? -1 : 1) + targets.length) % targets.length].focus();
          }
        }
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          go(current + (event.key === 'ArrowRight' ? 1 : -1));
        }
      });
      on(track, 'scroll', () => {
        current = Math.max(0, Math.min(slides.length - 1, Math.round(track.scrollLeft / track.clientWidth)));
        update();
      });
      const observer = new ResizeObserver(() => go(current, false));
      root.closest('.ea-page').append(dialog);
      document.documentElement.style.overflow = 'hidden';
      Object.assign(document.body.style, { position: 'fixed', top: `-${scrollY}px`, width: '100%' });
      dialog.showModal();
      observer.observe(track);
      update();
    });
  });
  return () => { dismiss(); all('[data-ea-gallery-open]', root).forEach(trigger => { trigger.hidden = true; }); };
}

function initialize() {
  cleanup();
  const hero = select('[data-launch-at]');
  const capture = select('[data-ea-capture]');
  const shell = select('.ea-shell');
  if (!hero || !capture || !shell) return;
  const abort = new AbortController();
  const { signal } = abort;
  const listen = (target, event, handler, options = {}) => target?.addEventListener(event, handler, { ...options, signal });
  const launchString = hero.dataset.launchAt;
  const explicitOffset = /T\d{2}:\d{2}(:\d{2})?(?:Z|[+-]\d{2}:\d{2})$/.test(launchString);
  const launch = explicitOffset ? Date.parse(launchString) : NaN;
  const validLaunch = Number.isFinite(launch);
  const textAt = launch - 10 * 60 * 1000;
  const timezone = hero.dataset.eaTimezone || 'America/New_York';
  const suffix = hero.dataset.eaTimeSuffix || 'EST';
  const config = capture.dataset;
  const password = select('#ea-password');
  const toggle = select('[data-ea-toggle-password]');
  const button = select('[data-ea-subscribe]', capture);
  const error = select('[data-ea-error]', capture);
  const sticky = select('[data-ea-sticky]');
  const stickyButton = select('[data-ea-sticky-cta]');
  const embeddedHost = select('[data-ea-embedded-host]', capture);
  const embeddedStatus = select('[data-ea-embedded-status]', capture);
  const popupFallback = select('[data-ea-popup-fallback]', capture);
  const inlineSignup = Boolean(config.eaFormId && embeddedHost);
  let mountTimer;
  let serverEpoch = Date.now();
  let monotonicEpoch = performance.now();
  const now = () => serverEpoch + performance.now() - monotonicEpoch;
  let phase = '';
  let subscribed = '';
  let opening = false;
  let awaitingSignup = false;
  let focused = false;
  const scope = `${config.eaShopId}:${launchString}`;
  try {
    if (localStorage.getItem('ea_subscribed_launch') === scope) subscribed = localStorage.getItem('ea_subscribed') || '';
  } catch {}

  const timeLabel = (time) => {
    try {
      return new Intl.DateTimeFormat('en-US', { timeZone: timezone, hour: 'numeric', minute: '2-digit', hour12: true }).format(time).replace(':00', '').replace(/\s/g, '').toLowerCase() + ` ${suffix}`;
    } catch { return ''; }
  };
  const textTime = validLaunch ? timeLabel(textAt) : '';
  const launchTime = validLaunch ? timeLabel(launch) : '';
  const replaceTimes = (text) => text.replaceAll('{text_time}', textTime || '11:50am EST').replaceAll('{launch_time}', launchTime || '12pm EST');
  all('[data-ea-time-copy]').forEach(el => {
    if (!el.dataset.eaOriginalCopy) el.dataset.eaOriginalCopy = el.innerHTML;
    el.innerHTML = replaceTimes(el.dataset.eaOriginalCopy);
  });
  if (textTime) all('[data-ea-text-time]').forEach(el => { el.textContent = textTime; });

  function showPassword(show, focus = false) {
    if (!password) return;
    password.hidden = !show;
    toggle?.setAttribute('aria-expanded', String(show));
    if (focus) {
      scrollTo(password);
      select('input[type="password"]', password)?.focus({ preventScroll: true });
    }
  }

  function scrollTo(target) {
    target?.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'start' });
  }

  function render() {
    const live = phase === 'live';
    const late = phase === 'late';
    select('[data-ea-signup]', capture).hidden = Boolean(subscribed) || late || live;
    select('[data-ea-success]', capture).hidden = !subscribed || late || live;
    select('[data-ea-late]', capture).hidden = !late;
    select('[data-ea-live]', capture).hidden = !live;
    if (subscribed) {
      const hasPhone = /^\+1 ••• ••• \d{4}$/.test(subscribed);
      select('[data-ea-success-message]', capture).textContent = replaceTimes(hasPhone ? config.eaSuccessBody.replaceAll('{phone}', subscribed) : config.eaSuccessGeneric);
    }
    if (stickyButton && sticky) {
      stickyButton.setAttribute('aria-busy', String(opening));
      stickyButton.setAttribute('aria-disabled', String(opening));
      stickyButton.textContent = opening ? sticky.dataset.eaLoadingLabel : live ? sticky.dataset.eaPasswordCta : subscribed ? sticky.dataset.eaSubscribed : late ? sticky.dataset.eaPasswordCta : sticky.dataset.eaCta;
      const enterPhone = inlineSignup && !subscribed && !late && !live;
      stickyButton.classList.toggle('ea-enter-phone', enterPhone);
      if (enterPhone) stickyButton.textContent = sticky.dataset.eaPhoneLabel;
      stickyButton.classList.toggle('is-subscribed', Boolean(subscribed) && !live);
      stickyButton.href = live || late ? '#ea-password' : '#ea-capture';
      select('[data-ea-clock-label]', sticky).textContent = live ? sticky.dataset.eaLiveLabel : sticky.dataset.eaLabel;
    }
  }

  function tick() {
    const current = now();
    const nextPhase = !validLaunch ? 'invalid' : current >= launch ? 'live' : current >= textAt ? 'late' : 'before';
    if (phase !== nextPhase) {
      phase = nextPhase;
      if (phase === 'live' || phase === 'late') showPassword(true);
      render();
    }
    const seconds = Math.max(0, Math.ceil((launch - current) / 1000));
    const days = Math.floor(seconds / 86400);
    const hh = String(Math.floor(seconds / 3600) % 24).padStart(2, '0');
    const mm = String(Math.floor(seconds / 60) % 60).padStart(2, '0');
    const ss = String(seconds % 60).padStart(2, '0');
    all('[data-ea-clock]').forEach(clock => {
      if (!validLaunch || phase === 'live') {
        clock.textContent = !validLaunch ? '—' : sticky?.dataset.eaLiveValue || 'Live now';
      } else {
        clock.replaceChildren(document.createTextNode(`${days ? `${days}d ` : ''}${hh}:${mm}:`));
        const accent = document.createElement('span');
        accent.className = 'ea-clock-seconds';
        accent.textContent = ss;
        clock.append(accent);
      }
    });
  }

  async function syncClock() {
    const started = performance.now();
    const timeout = new AbortController();
    const timer = setTimeout(() => timeout.abort(), 5000);
    const cancel = () => timeout.abort();
    signal.addEventListener('abort', cancel, { once: true });
    try {
      const response = await fetch('/', { method: 'HEAD', cache: 'no-store', credentials: 'same-origin', signal: timeout.signal });
      const epoch = Date.parse(response.headers.get('Date'));
      if (response.ok && Number.isFinite(epoch) && !signal.aborted) {
        serverEpoch = epoch + (performance.now() - started) / 2;
        monotonicEpoch = performance.now();
        tick();
      }
    } catch {} finally {
      clearTimeout(timer);
      signal.removeEventListener('abort', cancel);
    }
  }

  function signupError(reason) {
    error.textContent = config.eaErrorMessage;
    error.hidden = false;
    track('ea_phone_error', { reason });
  }

  function confirmSignup(isNew) {
    if (subscribed || phase === 'late' || phase === 'live') return;
    subscribed = 'confirmed';
    try {
      localStorage.setItem('ea_subscribed', subscribed);
      localStorage.setItem('ea_subscribed_launch', scope);
    } catch {}
    if (isNew) track('ea_phone_success', { source: 'ea-fall-performance' });
    render();
  }

  function embeddedFailure(reason) {
    if (signal.aborted || subscribed || phase === 'late' || phase === 'live') return;
    clearTimeout(mountTimer);
    embeddedStatus.hidden = true;
    embeddedHost.hidden = true;
    popupFallback.hidden = false;
    error.textContent = config.eaInlineError;
    error.hidden = false;
    track('ea_phone_error', { reason });
  }

  listen(window, 'postscript:embedded-form', event => {
    const detail = event.detail || {};
    if (!inlineSignup || String(detail.shopId) !== config.eaShopId || String(detail.formId) !== config.eaFormId) return;
    if (detail.type === 'mount' || detail.type === 'impression') {
      clearTimeout(mountTimer);
      embeddedHost.dataset.eaMounted = 'true';
      embeddedHost.hidden = false;
      embeddedStatus.hidden = true;
      popupFallback.hidden = true;
      error.hidden = true;
    }
    if (detail.type === 'complete' && detail.subscriberId && ['new', 'existing'].includes(detail.subscriberStatus)) {
      clearTimeout(mountTimer);
      confirmSignup(detail.subscriberStatus === 'new');
    }
  });

  async function startEmbeddedSignup() {
    if (!inlineSignup || subscribed || phase === 'late' || phase === 'live') return;
    if (embeddedHost.dataset.eaMounted === 'true') return;
    try {
      await loadPostscript(config.eaShopId);
      await loadEmbeddedForm();
      if (signal.aborted || subscribed || phase === 'late' || phase === 'live') return;
      mountTimer = setTimeout(() => embeddedFailure('embedded_mount_timeout'), 15000);
      if (!select('postscript-embedded-form', embeddedHost)) {
        const form = document.createElement('postscript-embedded-form');
        form.setAttribute('shop-id', config.eaShopId);
        form.setAttribute('form-id', config.eaFormId);
        embeddedHost.append(form);
      }
    } catch {
      embeddedFailure('embedded_load');
    }
  }

  listen(button, 'focus', () => { loadPostscript(config.eaShopId).catch(() => {}); });
  listen(button, 'pointerenter', () => { loadPostscript(config.eaShopId).catch(() => {}); }, { once: true });
  async function openSignup(source) {
    if (opening || phase === 'live' || phase === 'late') return;
    opening = true;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    error.hidden = true;
    render();
    try {
      if (!config.eaPopupId || !config.eaShopId) throw new Error('configuration');
      const sdk = await loadPostscript(config.eaShopId);
      if (signal.aborted || phase === 'live' || phase === 'late') return;
      awaitingSignup = true;
      await sdk.popups.open(config.eaPopupId, { activePopupBehavior: 'ALWAYS_DISMISS', respectPopupStatus: false, respectCooldown: false });
    } catch (failure) {
      awaitingSignup = false;
      if (!signal.aborted) {
        signupError(failure.message || 'sdk_open');
        if (source === 'sticky') {
          scrollTo(capture);
          error.tabIndex = -1;
          error.focus({ preventScroll: true });
        }
      }
    } finally {
      opening = false;
      button.disabled = false;
      button.setAttribute('aria-busy', 'false');
      if (!signal.aborted) render();
    }
  }
  listen(button, 'click', () => openSignup('capture'));

  listen(window, 'postscriptPopup', event => {
    const detail = event.detail || {};
    if (String(detail.popupId) !== config.eaPopupId || !awaitingSignup) return;
    if (detail.type === 'subscriberCreated' && detail.subscriberId) {
      confirmSignup(true);
    }
  });

  function isPopupInput(target) {
    return awaitingSignup && target instanceof HTMLInputElement && target.type === 'tel' && !capture.contains(target);
  }
  listen(document, 'focusin', event => {
    if (!focused && isPopupInput(event.target)) {
      focused = true;
      track('ea_phone_focus');
    }
  });
  listen(document, 'submit', event => {
    if (!awaitingSignup || event.target.id === 'ea-password-form') return;
    if (event.target.querySelector('input[type="tel"]')) track('ea_phone_submit');
  }, { capture: true });
  listen(toggle, 'click', event => {
    event.preventDefault();
    track('ea_password_toggle');
    showPassword(password.hidden, password.hidden);
  });
  listen(select('#ea-password-form'), 'submit', () => track('ea_password_submit'));
  all('[data-ea-jump]').forEach(link => listen(link, 'click', event => {
    const target = document.getElementById(link.dataset.eaJump);
    if (!target) return;
    event.preventDefault();
    track('ea_jump_click', { section: link.dataset.eaJump });
    scrollTo(target);
    target.tabIndex = -1;
    target.focus({ preventScroll: true });
  }));
  listen(stickyButton, 'click', event => {
    event.preventDefault();
    track('ea_sticky_cta_click');
    if (phase === 'live' || phase === 'late') showPassword(true, true);
    else if (subscribed) {
      scrollTo(capture);
      select('[data-ea-success]', capture)?.focus({ preventScroll: true });
    } else if (inlineSignup) {
      scrollTo(capture);
      const form = select('postscript-embedded-form', embeddedHost);
      const input = form?.shadowRoot?.querySelector('input[type="tel"],input[autocomplete="one-time-code"]');
      (input || (popupFallback.hidden ? embeddedHost : button)).focus({ preventScroll: true });
    } else openSignup('sticky');
  });

  const markers = [];
  let desktop;
  function restoreLayout() {
    [...markers].reverse().forEach(({ marker, node }) => marker.replaceWith(node));
    markers.length = 0;
    desktop?.remove();
    desktop = undefined;
  }
  function layout() {
    restoreLayout();
    if (!media.matches) return;
    const heroSection = hero.closest('.ea-section');
    const captureSection = capture.closest('.ea-section');
    const collectionSections = all('.ea-section--jump-bar, .ea-section--products', shell);
    const stickySection = sticky?.closest('.ea-section');
    if (!heroSection || !captureSection || !collectionSections.length) return;
    desktop = document.createElement('div');
    desktop.className = 'ea-desktop';
    const intro = document.createElement('div');
    const collection = document.createElement('div');
    const toolbar = document.createElement('div');
    intro.className = 'ea-desktop-intro';
    collection.className = 'ea-desktop-collection';
    toolbar.className = 'ea-desktop-toolbar';
    heroSection.before(desktop);
    desktop.append(intro, collection);
    collection.append(toolbar);
    const move = (node, parent) => {
      const marker = document.createComment('ea-position');
      node.before(marker);
      markers.push({ marker, node });
      parent.append(node);
    };
    move(heroSection, intro);
    move(captureSection, intro);
    collectionSections.forEach(node => move(node, node.matches('.ea-section--jump-bar') ? toolbar : collection));
    if (stickySection) move(stickySection, toolbar);
  }

  showPassword(Boolean(select('[data-ea-password-error]')) || location.hash === '#ea-password');
  tick();
  startEmbeddedSignup();
  syncClock();
  const interval = setInterval(tick, 1000);
  listen(document, 'visibilitychange', () => { if (!document.hidden) { tick(); syncClock(); } });
  listen(window, 'pageshow', () => { tick(); syncClock(); });
  listen(window, 'storage', event => {
    if (event.key?.startsWith('ea_subscribed')) {
      try { subscribed = localStorage.getItem('ea_subscribed_launch') === scope ? localStorage.getItem('ea_subscribed') || '' : ''; } catch {}
      render();
    }
  });
  const cleanupGalleries = setupGalleries(shell, listen);
  layout();
  listen(media, 'change', layout);
  if (!viewTracked) { track('ea_view'); viewTracked = true; }
  cleanup = () => { cleanupGalleries(); abort.abort(); clearInterval(interval); clearTimeout(mountTimer); restoreLayout(); };
}

initialize();
document.addEventListener('shopify:section:load', initialize);
document.addEventListener('shopify:section:unload', () => { cleanup(); queueMicrotask(initialize); });
document.addEventListener('shopify:section:reorder', initialize);
